import Vue from 'vue';
import nativeAudioPlayListener from './nativeAudioPlayListener';
import { getLangValue } from '@/tools/languageValue';
import { PLAYER_STATE } from '@/tools/consts';
import { uiComponents, uiStates } from '@/tools/viewState';
import { mutationTypes, actionTypes, errorTypes, getterTypes } from '@/types';
import { logger } from '@/tools';
import * as mediaSyncronization from '@/tools/mediaSyncronization';

export default {
	state: {
		mediaID: null,
		loadedMediaObject: null,
		source: null,
		title: '',
		description: '',
		duration: 0.0,
		currentTime: 0.0,
		playerState: PLAYER_STATE.emtpy,
		startAt: null,
		open: false,
		synced: false,
		setCurrentTime: true,
		isSynced: false,
		audioPlayer: null,
		startTime: null,
		audioAutoPlay: false,
	},
	getters: {
		// AudioPlayer Values
		[getterTypes.activeMediaID](state) {
			return state.playerState !== PLAYER_STATE.stopped &&
				state.playerState !== PLAYER_STATE.ended &&
				state.playerState !== PLAYER_STATE.paused &&
				state.playerState !== PLAYER_STATE.emtpy
				? state.mediaID
				: null;
		},
		[getterTypes.audioSource](state) {
			return state.source;
		},
		[getterTypes.audioIsActive](state) {
			return (
				state.playerState !== PLAYER_STATE.stopped &&
				state.playerState !== PLAYER_STATE.ended &&
				state.playerState !== PLAYER_STATE.paused &&
				state.playerState !== PLAYER_STATE.emtpy
			);
		},
	},
	mutations: {
		[mutationTypes.audioSetSource](
			state,
			{ source, title, duration, description, mediaID, startPlaying, position, isSynced }
		) {
			// If position is not set and source is the same as the
			// audio file already loaded do not change position
			if (state.source !== source) {
				state.setCurrentTime = typeof position !== 'undefined' ? position : 0.0;
			} else if (typeof position !== 'undefined') {
				// FIXME Rank why is undefined a string here, this does not look correct
				state.setCurrentTime = position;
			}
			Object.assign(state, { mediaID, source, title, description, duration, isSynced });
			state.open = true;
			if (startPlaying) {
				state.playerState = PLAYER_STATE.playing;
			}
		},
		[mutationTypes.audioSetState](state, { playerState }) {
			state.playerState = playerState;
		},
		[mutationTypes.resetStartAt](state) {
			state.startAt = null;
		},
		[mutationTypes.audioSetCurrentTime](state, { currentTime, clearSetCurrentTime }) {
			if (clearSetCurrentTime) {
				state.setCurrentTime = null;
			}
			state.currentTime = currentTime;
		},
		[mutationTypes.audioStart](state, { position }) {
			state.open = true;
			state.audioPlayer.muted = false;
			if (position) state.currentTime = position;
			state.playerState = PLAYER_STATE.playing;
		},
		[mutationTypes.audioPause](state) {
			state.playerState = PLAYER_STATE.paused;
		},
		[mutationTypes.audioStop](state) {
			state.playerState = PLAYER_STATE.stopped;
		},
		[mutationTypes.audioClose](state) {
			state.open = false;
		},
		[mutationTypes.audioTogglePlay](state) {
			let result = PLAYER_STATE.emtpy;
			switch (state.playerState) {
				case PLAYER_STATE.stopped:
				case PLAYER_STATE.ended:
				case PLAYER_STATE.paused:
					result = PLAYER_STATE.playing;
					break;
				case PLAYER_STATE.playing:
				case PLAYER_STATE.loading:
				case PLAYER_STATE.syncing:
					result = PLAYER_STATE.paused;
			}
			state.playerState = result;
			// audio media handling happens in audioPlayer plugin
		},
		[mutationTypes.audioSetPlayer](state, player) {
			state.audioPlayer = player;
		},
		[mutationTypes.audioAutoPlay](state, { autoPlay }) {
			state.audioAutoPlay = autoPlay;
		},
	},
	actions: {
		[actionTypes.audioTogglePlay]({ state, getters, commit, dispatch }, options = {}) {
			const { mediaID, isSynced } = options;
			if (!mediaID || getters[getterTypes.activeMediaID] == mediaID) {
				if (state.isSynced) {
					mediaSyncronization.onClickTogglePlay();
				} else {
					commit(mutationTypes.audioTogglePlay);
				}
			} else {
				logger.log('Audio audioLoadAndPlayByMediaID');
				dispatch(actionTypes.audioLoadAndPlayByMediaID, { mediaID, isSynced });
			}
		},
		/**
		 * Action to close audioplayer
		 */
		[actionTypes.audioClose]({ commit }) {
			commit(mutationTypes.audioStop);
			commit(mutationTypes.viewStateSetUiState, { id: uiComponents.uiAudio, uiState: uiStates.hidden });
			commit(mutationTypes.audioClose);
		},
		/**
		 * Action to load MediaObject by id but it has to be in the current MediaObjectMap
		 * @param {*} store
		 * @param {mediaID,position} parameters
		 */
		[actionTypes.audioLoadAndPlayByMediaID](
			{ dispatch, rootState },
			{ mediaID, position, isSynced, suppressErrors }
		) {
			const mediaObject = rootState.currentMediaObjectMap.get(mediaID);
			if (!mediaObject) {
				if (!suppressErrors) {
					dispatch(actionTypes.sendErrorMessage, { error: new Error(errorTypes.unkownMediaObject) });
				}
				return;
			}
			let duration = mediaObject.Duration ? Number(getLangValue(mediaObject.Duration)) : 0;
			if (isSynced) {
				duration = Object.values(mediaObject.Duration)[0];
			}
			dispatch(actionTypes.audioLoadAndPlay, {
				source: mediaObject.Sources ? getLangValue(mediaObject.Sources) : null,
				title: mediaObject.Title ? getLangValue(mediaObject.Title) : '',
				description: mediaObject.Desc ? getLangValue(mediaObject.Desc) : '',
				duration,
				mediaID,
				position,
				isSynced,
				startTime: mediaObject.startTime ? mediaObject.startTime : undefined,
				startTimeFunction: mediaObject.startTimeFunction ? mediaObject.startTimeFunction : null,
			});
		},
		/**
		 * Action to load and start Audio file by all mediaFile attributes
		 * With position it is possible to set the starting position in seconds
		 *
		 * @param {*} store
		 * @param {mediaID, source, title, duration, position} parameters
		 */
		[actionTypes.audioLoadAndPlay](
			{ commit, rootState, state },
			{ mediaID, source, title, duration, description, position, isSynced, startTime, startTimeFunction }
		) {
			if (!source) throw new Error('audioLoadAndPlay: source is missing.');
			// TODO Error handling
			// If the audio is already playing stop it first so we can
			// exchange the source and start a new audio.
			if (
				(state.playerState === PLAYER_STATE.playing || state.playerState === PLAYER_STATE.loading) &&
				!isSynced
			) {
				setTimeout(() => {
					commit(mutationTypes.audioStop);
					setTimeout(() => {
						commit(mutationTypes.audioStart, {});
					}, 0);
				}, 0);
			}
			if (isSynced) {
				Vue.nextTick(() => {
					mediaSyncronization.initializeSyncronization({
						dataSourceURL: rootState.dataSourceURL,
						$media: state.audioPlayer,
						mediaDuration: duration,
						startPlaying: true,
						src: source,
						mediaType: 'audio',
						startTime,
						startTimeFunction,
						commit,
					});
					commit(mutationTypes.audioStart, {});
				});
			}
			// The audio was synced before we now need to unbind the sync function
			else if (state.synced) {
				mediaSyncronization.unSync();
			}
			setTimeout(() => {
				// VUE next tick is here not working so use timeout to be sure the audio has stopped
				commit(mutationTypes.audioSetSource, {
					source,
					title,
					description,
					duration,
					mediaID,
					rootState,
					position,
					startPlaying: !isSynced,
					isSynced,
				});
				commit(mutationTypes.viewStateSetUiState, {
					// show audioplayer
					id: uiComponents.uiAudio,
					uiState: uiStates.maximised,
				});
			}, 0);
		},
		[actionTypes.audioAutoPlay]({ commit }, { autoPlay }) {
			commit(mutationTypes.audioAutoPlay, { autoPlay });
		},
	},
	plugins: [nativeAudioPlayListener],
};

window.nativeOnContentShouldRestartPlayingStream = () => {
	// TODO: check if it is still needed and in what circum stances
	// and implement accordingly
};
